
import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Review } from '@/lib/types';
import { useReviewStore } from '@/lib/stores/reviewStore';

type CompanySearchResult = { companyName: string, reviewCount: number };

interface CompanySearchProps {
  onCompanySelect: (companyName: string) => void;
}

const CompanySearch = ({ onCompanySelect }: CompanySearchProps) => {
  const { toast } = useToast();
  const { reviews: localReviews } = useReviewStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([]);

  // Search for companies with reviews
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast({
        title: "Search term required",
        description: "Please enter a company name to search for.",
        variant: "destructive"
      });
      return;
    }

    setIsSearching(true);
    
    try {
      // Search in Supabase - using a different approach to avoid the group() method
      // First, get all reviews that match the company name pattern
      const { data: supabaseMatchingReviews, error } = await supabase
        .from('reviews')
        .select('company')
        .ilike('company', `%${searchTerm}%`);
        
      if (error) throw error;
      
      // Also search in the companies table to ensure we find all companies
      const { data: matchingCompanies, error: companiesError } = await supabase
        .from('companies')
        .select('name')
        .ilike('name', `%${searchTerm}%`);
        
      if (companiesError) throw companiesError;
      
      // Then manually count them by company
      const companyCount: Record<string, number> = {};
      
      // Count reviews per company
      supabaseMatchingReviews?.forEach(review => {
        const company = review.company;
        companyCount[company] = (companyCount[company] || 0) + 1;
      });
      
      // Add companies without reviews (with count 0)
      matchingCompanies?.forEach(company => {
        if (!companyCount[company.name]) {
          companyCount[company.name] = 0;
        }
      });
      
      // Convert to the expected format
      const supabaseResults = Object.entries(companyCount).map(([company, count]) => ({
        company,
        count: count.toString()
      }));
      
      // Search in local store
      const localResults = localReviews
        .filter(review => review.company.toLowerCase().includes(searchTerm.toLowerCase()))
        .reduce((acc, review) => {
          const existingCompany = acc.find(c => c.companyName === review.company);
          if (existingCompany) {
            existingCompany.reviewCount += 1;
          } else {
            acc.push({ companyName: review.company, reviewCount: 1 });
          }
          return acc;
        }, [] as CompanySearchResult[]);
      
      // Combine results, merging counts for the same company
      const combinedResults = [...localResults];
      
      supabaseResults?.forEach(result => {
        const existingCompany = combinedResults.find(c => c.companyName.toLowerCase() === result.company.toLowerCase());
        if (existingCompany) {
          existingCompany.reviewCount += parseInt(result.count);
        } else {
          combinedResults.push({ 
            companyName: result.company, 
            reviewCount: parseInt(result.count) 
          });
        }
      });
      
      setSearchResults(combinedResults);
    } catch (error) {
      console.error("Error searching for companies:", error);
      toast({
        title: "Search failed",
        description: "There was an error searching for companies.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-medium mb-6">Search Reviews by Company</h2>
      
      <div className="flex gap-2 mb-8">
        <Input
          placeholder="Enter company name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
        <Button 
          onClick={handleSearch}
          disabled={isSearching || !searchTerm.trim()}
        >
          {isSearching ? "Searching..." : "Search"}
          {!isSearching && <Search className="ml-2 h-4 w-4" />}
        </Button>
      </div>
      
      {searchResults.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Review Count</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {searchResults.map((result) => (
                <TableRow key={result.companyName}>
                  <TableCell className="font-medium">{result.companyName}</TableCell>
                  <TableCell>{result.reviewCount}</TableCell>
                  <TableCell className="text-right">
                    <Button 
                      size="sm" 
                      onClick={() => onCompanySelect(result.companyName)}
                    >
                      View Reviews
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      {searchTerm && searchResults.length === 0 && !isSearching && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-redflags-gray">No companies found matching "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default CompanySearch;
